'use client'

/**
 * Suspense-compatible hooks - data is guaranteed defined when component renders.
 * These hooks also seed related entity caches for instant navigation.
 */
import { useEffect } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'

import {
  useAgencyQueries,
  useAwardQueries,
  usePersonQueries,
  useRecipientQueries,
} from './queryOptions'

export function useSuspenseAward(id: string) {
  const queries = useAwardQueries()
  const result = useSuspenseQuery(queries.detail(id))

  // Seed related previews (agencies, recipient) for instant skeleton rendering
  useEffect(() => {
    if (result.data) {
      queries.seedRelatedPreviews(result.data)
    }
  }, [result.data, queries])

  return result
}

export function useSuspenseAwardTransactions(
  awardId: string,
  page: number = 1,
  limit: number = 25,
) {
  const queries = useAwardQueries()
  return useSuspenseQuery(queries.transactions(awardId, page, limit))
}

export function useSuspenseAwardSubawards(
  awardId: string,
  page: number = 1,
  limit: number = 25,
) {
  const queries = useAwardQueries()
  return useSuspenseQuery(queries.subawards(awardId, page, limit))
}

export function useSuspenseRecipient(id: string) {
  const queries = useRecipientQueries()
  const result = useSuspenseQuery(queries.detail(id))

  // Seed award previews from recent awards for instant skeleton rendering
  useEffect(() => {
    if (result.data?.recentAwards) {
      queries.seedRecentAwardsPreviews(result.data.recentAwards)
    }
  }, [result.data, queries])

  return result
}

export function useSuspenseAgency(id: string) {
  const queries = useAgencyQueries()
  const result = useSuspenseQuery(queries.detail(id))

  // Seed award previews from recent opportunities for instant skeleton rendering
  useEffect(() => {
    if (result.data?.recentOpportunities) {
      queries.seedRecentOpportunitiesPreviews(result.data.recentOpportunities)
    }
  }, [result.data, queries])

  return result
}

export function useSuspensePerson(id: string) {
  const queries = usePersonQueries()
  return useSuspenseQuery(queries.detail(id))
}
